import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowRightLeft, CircleAlert } from 'lucide-react';
import { Button, Dialog, Field, Input, Select } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useNodes } from '@/hooks/useNodes';
import { useMigrateSite } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { canReceiveSite } from '@/utils/nodes';
import type { SiteDetail } from '@/types/api';

/**
 * Moving a site to another server (TZ section 10.5).
 *
 * A dialog, not a drawer: one choice and one confirmation. What happens to the
 * site is written out step by step, because the operator is agreeing to a few
 * minutes of maintenance and should know that before, not after.
 */
export function MigrateSiteDialog({
  site,
  open,
  onClose,
}: {
  readonly site: SiteDetail;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [target, setTarget] = useState('');
  const [typed, setTyped] = useState('');
  // One key per opened dialog: a double click or a retry is the same move.
  const key = useRef(idempotencyKey('site.migrate'));

  const nodes = useNodes({ enabled: open });
  const candidates = (nodes.data ?? []).filter((node) => canReceiveSite(node, site));

  const migrate = useMigrateSite(site.id, {
    onSuccess: () => {
      toast.success('Перенос поставлен в очередь');
      key.current = idempotencyKey('site.migrate');
      setTyped('');
      onClose();
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!typed.trim() || migrate.isPending || candidates.length === 0) return;
    migrate.mutate({ input: { ...(target ? { targetNodeId: target } : {}), confirmation: typed.trim() }, key: key.current });
  };

  return (
    <Dialog
      open={open}
      title="Перенести сайт на другой сервер"
      description={`Сейчас: ${site.nodeName ?? 'сервер не указан'}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={migrate.isPending}>
            Отмена
          </Button>
          <Button
            type="submit"
            form="migrate-site"
            variant="primary"
            icon={ArrowRightLeft}
            busy={migrate.isPending}
            disabled={!typed.trim() || candidates.length === 0}
          >
            Перенести
          </Button>
        </>
      }
    >
      <form id="migrate-site" className="stack" onSubmit={submit} noValidate>
        {nodes.isSuccess && candidates.length === 0 ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            Нет других активных серверов.
          </p>
        ) : (
          <Field label="Куда" htmlFor="migrate-target">
            <Select
              id="migrate-target"
              label="Куда"
              value={target}
              onChange={setTarget}
              options={[
                { value: '', label: 'Автоматически', hint: 'Наименее загруженный подходящий' },
                ...candidates.map((node) => ({
                  value: node.id,
                  label: node.name,
                  hint: `${node.siteCount} сайт(ов) · свободно ${Math.max(0, Math.round((node.totalMemoryMb - node.usedMemoryMb) / 1024))} ГБ памяти`,
                })),
              ]}
            />
          </Field>
        )}

        <ol className="enroll-steps">
          <li>Сайт переводится в режим обслуживания.</li>
          <li>Создаётся копия и переносится на новый сервер.</li>
          <li>Сайт разворачивается и проверяется на новом сервере.</li>
          <li>DNS переключается на новый сервер. Прежняя копия останавливается, но не удаляется.</li>
        </ol>
        <p className="field-hint">Занимает несколько минут. При сбое сайт вернётся на прежний сервер.</p>

        <Field label="Для подтверждения введите идентификатор сайта" htmlFor="migrate-confirm" hint={site.slug}>
          <Input id="migrate-confirm" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder={site.slug} autoComplete="off" />
        </Field>

        {migrate.isError ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            {errorText(migrate.error, 'Не удалось поставить перенос в очередь')}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
